import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  authRoles,
  useOrderByFilter,
  useToQueryString,
} from '@/core'
import { useModuleQuery } from '../query/useModuleQuery'
import { TInvitation } from '@/modules/invitations'
import { useAuth } from 'vue-auth3'

const [useProvideModule, useModule] = createInjectionState(() => {
  const auth = useAuth()
  const form = ref<TInvitation>({})
  const filters = ref<ToQueryStringFilters>({
    start_date: '',
    end_date: '',
    state_id: [],
    city_id: [],
    lead_id: [],
    seller_id: [],
    disabled_pagination: true,
    order_by: 'delivery_at,desc',
  })
  const estimationCommentsFilters = ref<ToQueryStringFilters>({
    invitation_id: [],
  })

  const sellerCommentsFilters = ref<ToQueryStringFilters>({
    invitation_id: [],
  })

  const filesFilters = ref<ToQueryStringFilters>({
    invitation_id: '',
  })

  const citiesFilters = ref<ToQueryStringFilters>({
    param: '',
    state_id: '',
    order_by: 'name,asc',
  })

  const moduleQueryParams = useToQueryString(toReactive(filters))
  const cityQueryParams = useToQueryString(toReactive(citiesFilters))
  const estimationCommentsQueryParams = useToQueryString(
    toReactive(estimationCommentsFilters),
  )
  const sellerCommentsQueryParams = useToQueryString(
    toReactive(sellerCommentsFilters),
  )
  const filesQueryParams = useToQueryString(toReactive(filesFilters))

  const moduleQuery = useModuleQuery({
    filters: {
      loadRecords: moduleQueryParams.queryParams,
      citiesCatalog: cityQueryParams.queryParams,
      loadEstimationComments: estimationCommentsQueryParams.queryParams,
      loadSellerComments: sellerCommentsQueryParams.queryParams,
      loadFiles: filesQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: computed(() => moduleQueryParams.params.value.length > 2),
      citiesCatalog: computed(() => cityQueryParams.params.value.length > 1),
      leadsCatalog: true,
      loadEstimationComments: computed(
        () =>
          !!estimationCommentsQueryParams.params.value.length &&
          auth.check([authRoles.ESTIMATOR, authRoles.SELLER]),
      ),
      loadSellerComments: computed(
        () => !!sellerCommentsQueryParams.params.value.length,
      ),
      loadFiles: computed(() => !!filesQueryParams.params.value.length),
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at', 'email'],
  })

  const onLoadComments = (id: number) => {
    estimationCommentsFilters.value.invitation_id = [id]
  }

  const onLoadSellerComments = (id: number) => {
    sellerCommentsFilters.value.invitation_id = [id]
  }

  const onLoadFiles = (id: number) => {
    filesFilters.value.invitation_id = id
  }

  const invitations = computed(() => {
    const invitationsList = moduleQuery.loadRecords?.data.value

    return invitationsList?.map((inv: TInvitation, index: number) => {
      return {
        key: inv?.id ?? index,
        dates: [new Date(inv?.delivery_date ?? '')],
        customData: inv,
      }
    })
  })
  const states = computed(() => moduleQuery.statesCatalog.data?.value)
  const cities = computed(() => moduleQuery.citiesCatalog.data?.value)
  const leads = computed(() => moduleQuery.leadsCatalog.data?.value)
  const sellers = computed(() => moduleQuery.sellersCatalog.data?.value)
  const comments = computed(() => moduleQuery.loadEstimationComments.data.value)
  const sellerComments = computed(
    () => moduleQuery.loadSellerComments.data.value,
  )
  const files = computed(() => moduleQuery.loadFiles.data.value)
  const loaders = computed(() => ({
    isLoading: moduleQuery.loadRecords.isLoading.value,
    isCommentsLoading: moduleQuery.loadEstimationComments.isLoading.value,
    isCommentsSellerLoading: moduleQuery.loadSellerComments.isLoading.value,
    isFilesLoading: moduleQuery.loadFiles.isLoading?.value,
  }))

  return {
    comments,
    sellerComments,
    files,
    sellers,
    leads,
    invitations,
    filters,
    orderByOptions,
    form,
    loaders,
    states,
    cities,
    citiesFilters,
    onLoadComments,
    onLoadSellerComments,
    onLoadFiles,
  }
})

export { useProvideModule, useModule }
